import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf, Mail, Lock, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (user && user !== false) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      // Si on a une réponse HTTP avec détail, on l'affiche
      const detail = err?.response?.data?.detail;
      if (detail) {
        setError(formatApiErrorDetail(detail));
      } else if (err?.response?.status) {
        setError(`Erreur serveur (HTTP ${err.response.status}). Contactez l'administrateur.`);
      } else {
        // Pas de réponse = CORS / réseau / backend down
        setError("Impossible de contacter le serveur. Vérifiez votre connexion ou contactez l'administrateur.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4 relative overflow-hidden" data-testid="login-page">
      {/* Décor : gradient + grain */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F0] via-[#EFEEDF] to-[#E0E5DC]" aria-hidden="true" />
      <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-[#1F4E3D]/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-[#8C6B2F]/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" aria-hidden="true" />

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-11 h-11 rounded-lg bg-[#1F4E3D] flex items-center justify-center text-white shadow-sm">
            <Leaf className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-xl font-medium text-[#1A1C1A]">AgroManage</div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#575A57]">Production & Traçabilité</div>
          </div>
        </div>

        {/* Carte de connexion */}
        <form
          onSubmit={onSubmit}
          className="bg-white border border-[#E5E5E0] rounded-xl p-7 shadow-[0_8px_30px_rgba(31,78,61,0.08)]"
          data-testid="login-form"
        >
          <h1 className="font-heading text-2xl font-medium text-[#1A1C1A]">Connexion</h1>
          <p className="text-sm text-[#575A57] mt-1 mb-6">
            Identifiez-vous pour accéder à votre tableau de bord.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="mb-1.5 block text-[#1A1C1A]">Adresse email</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@maison-thai.pro"
                  required
                  className="pl-9"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="mb-1.5 block text-[#1A1C1A]">Mot de passe</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-9"
                  data-testid="login-password"
                />
              </div>
            </div>

            {error && (
              <div
                className="bg-[#FCEEEA] border border-[#C05C47]/30 text-[#8B3D2D] text-sm rounded-md px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !email || !password}
              className="w-full bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
              data-testid="login-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connexion…</>
              ) : (
                "Se connecter"
              )}
            </Button>

            <div className="text-center pt-2">
              <Link
                to="/mot-de-passe-oublie"
                className="text-sm text-[#575A57] hover:text-[#1F4E3D] underline"
                data-testid="login-forgot-password-link"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </div>
        </form>

        <p className="text-center text-xs text-[#575A57] mt-6">
          Espace privé — accès réservé à l'équipe Maison Thaï.
        </p>
      </div>
    </div>
  );
}
